let _ = t => t,
  _t;
import { EERIE, KOALA } from 'HubStyleTokens/colors';
import { Fragment, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { sortProductLines } from 'pricing-pages-lib/utils/sortingUtils';
import { getHighlightsForProductLine, getProductLevelFromApiNameString, getProductLineFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import breakpoint from '../../../constants/Breakpoints';
import { COMPARE_TRIAL_PLAN_TABLE } from '../../../constants/ErrorBoundaryNames';
import { SCREENS, UPGRADE_PAGES } from '../../../constants/tracking/Locations';
import { useFeatureStore } from '../../../hooks/useFeatureStore';
import { getAllFeaturesByAllProductLinesSelector } from '../../../selectors/FeatureSelectors';
import { highestProductLevelFromProductInventorySelector } from '../../../selectors/ProductInventorySelectors';
import { withErrorBoundary } from '../../../upgrade-page/ErrorBoundary';
import ScrollTrackingWrapper from '../../common/ScrollTrackingWrapper';
import FeatureTableRow from '../FeatureTableRow';
import { GradientOverlay } from '../SharedComponents';
import CompareTrialPlanTableStaticHeader from './CompareTrialPlanTableStaticHeader';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TableContainer = styled.div.withConfig({
  displayName: "CompareTrialPlanTable__TableContainer",
  componentId: "sc-1112riw-0"
})(["width:100%;padding:16px 0 60px;table{width:100%;border-spacing:0;table-layout:fixed;}table thead tr th:nth-child(1),table tbody tr th:nth-child(1){width:50%;border:1px solid ", ";}"], KOALA);
const paddingLeft = css(["padding-left:42px;", ""], breakpoint.tablet(_t || (_t = _`
    padding-left: 16px;
    `)));
const rowHeaderStyles = css(["text-align:left;", ""], paddingLeft);
const Table = styled.table.withConfig({
  displayName: "CompareTrialPlanTable__Table",
  componentId: "sc-1112riw-1"
})(["tbody tr{height:70px;}"]);
const TableContent = styled.tbody.withConfig({
  displayName: "CompareTrialPlanTable__TableContent",
  componentId: "sc-1112riw-2"
})(["position:relative;overflow:hidden;max-height:100%;& tr td{padding:16px 0;text-align:center;border:1px solid ", ";}& tr th[scope='row']{", "}"], KOALA, rowHeaderStyles);
const CompareTrialPlanTable = ({
  upgradePointApiName,
  purchaseMotions
}) => {
  const [expanded, setExpanded] = useState(false);
  const upgradePointProductLine = getProductLineFromApiNameString(upgradePointApiName);
  const upgradePointProductLevel = getProductLevelFromApiNameString(upgradePointApiName);
  const highlightedProducts = getHighlightsForProductLine(upgradePointProductLine);

  // determine highest owned level of productLine
  const productLevelOwned = useSelector(state => highestProductLevelFromProductInventorySelector(state, {
    productLine: upgradePointProductLine
  }));
  const filteredHighlightedProducts = highlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return [productLevelOwned, upgradePointProductLevel].includes(localProductLevel);
  });
  const productsByProductLine = {
    [upgradePointProductLine]: filteredHighlightedProducts
  };
  useFeatureStore({
    highlightedProducts: filteredHighlightedProducts,
    productLine: upgradePointProductLine
  });
  const features = useSelector(getAllFeaturesByAllProductLinesSelector);
  const featureTableRef = useRef(null);
  if (!Object.keys(features).length) return null;
  const sortedProductLines = sortProductLines(Object.keys(features));
  const levels = new Set();
  Object.values(productsByProductLine).forEach(products => {
    products.forEach(product => {
      const level = getProductLevelFromApiNameString(product);
      levels.add(level);
    });
  });
  const levelsAndApiNames = Object.entries(productsByProductLine).flatMap(([product, apiNames]) => apiNames ? apiNames.map(apiName => ({
    productLine: product,
    level: getProductLevelFromApiNameString(apiName)
  })) : []);
  const handleExpandClick = () => {
    const expandCollapseAction = expanded ? 'collapse' : 'expand';
    getUsageTracker().track('upgradesInteraction', {
      action: `clicked ${expandCollapseAction} trial compare table`,
      apiName: upgradePointApiName
    });
    setExpanded(prevExpandedState => !prevExpandedState);
  };
  return /*#__PURE__*/_jsxs(ScrollTrackingWrapper, {
    scrollTrackingFn: () => {
      getUsageTracker().track('scrollInteraction', {
        action: 'scrolled to trial compare table',
        apiName: upgradePointApiName,
        screen: SCREENS.UPGRADE_PAGE,
        subscreen: UPGRADE_PAGES.TRIAL,
        name: 'compare table'
      });
    },
    children: [/*#__PURE__*/_jsx(GradientOverlay, {
      expanded: expanded,
      children: /*#__PURE__*/_jsx(TableContainer, {
        ref: featureTableRef,
        columns: levels.size,
        "data-selenium": "compare-trial-plan-table",
        id: "compare-trial-plan-table",
        children: /*#__PURE__*/_jsx(Table, {
          children: sortedProductLines.map(sortedProductLine => {
            const localFeatures = features[sortedProductLine];
            const localHighlightedProducts = productsByProductLine[sortedProductLine];
            if (localHighlightedProducts === undefined || localHighlightedProducts.length <= 0) return null;
            return /*#__PURE__*/_jsxs(Fragment, {
              children: [/*#__PURE__*/_jsx(CompareTrialPlanTableStaticHeader, {
                levelsAndApiNames: levelsAndApiNames,
                upgradePointProductLevel: upgradePointProductLevel,
                upgradePointApiName: upgradePointApiName,
                purchaseMotions: purchaseMotions
              }), /*#__PURE__*/_jsx(TableContent, {
                id: `compare-trial-plan-table-${sortedProductLine}-section`,
                children: localFeatures.map(feature => {
                  return /*#__PURE__*/_jsx(FeatureTableRow, {
                    highlightedProducts: localHighlightedProducts,
                    feature: feature,
                    productLine: sortedProductLine
                  }, feature.featureId);
                })
              })]
            }, sortedProductLine);
          })
        })
      })
    }), /*#__PURE__*/_jsx("div", {
      className: "text-center justify-center align-center",
      children: /*#__PURE__*/_jsxs(UIButton, {
        use: "transparent",
        onClick: handleExpandClick,
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: expanded ? 'featureTable.collapse' : 'featureTable.expand'
        }), /*#__PURE__*/_jsx(UIIcon, {
          name: expanded ? 'up' : 'down',
          size: "xxs",
          color: EERIE
        })]
      })
    })]
  });
};
export default withErrorBoundary(CompareTrialPlanTable, {
  boundaryName: COMPARE_TRIAL_PLAN_TABLE
});